import {
    ref, computed, Ref, watch, nextTick
} from 'vue';
import { tz } from 'moment-timezone';
import { getUserData } from '@/utils/userData';
import { getUserTimeFormatPreference } from '@/utils/userPreferences';
import type { TimeFormatPreference } from '@/utils/timeFormat';
import { updateUrlParams, buildQueryFromUrl } from '../utils/urlParamsUtils';
import { updateFilterState, clearFilterState } from '../utils/filterSessionStorage';
import { Query, SearchType } from '../types/definitionsType';
import type PagePreference from '@agyletime/vue-3-common/src/typings/pagePreference';
import { useLogger } from '@agyletime/vue-3-common/src/composables/logger';
import type { InjectedPagePreferences } from '@agyletime/vue-3-common/src/typings/pagePreference';
import type { WeekDay } from './useWeekStartDay';
import {
    initializeFromUrlQuery,
    buildInitialQuery,
    buildUrlParamsFromQuery,
    updatePagePreferences as updatePagePreferencesUtils,
    setPageTitles
} from '../utils/teamScheduleUtils';

export function useTeamSchedule(pagePreferences: InjectedPagePreferences, weekStartDay: Ref<WeekDay>) {
    const logger = useLogger('TeamSchedule');

    // Default values for view settings
    const DEFAULT_COLOR_MODE: 'TEMPLATES' | 'LOCATIONS' | 'NONE' = 'TEMPLATES';
    const DEFAULT_GROUP_BY: 'TEAMS' | 'NONE' = 'NONE';
    const DEFAULT_COLUMN_DISPLAY = { userId: false, email: false };

    /**
     * Stored reference to the selected view filter for reliable restoration
     * This avoids race conditions when reading from pagePreferences.selected.value
     */
    let pendingViewFilter: Query | null = null;

    /**
     * Initialize settings from loaded view preferences.
     * Restores colorMode, groupBy, and columnDisplay from saved view.
     * Follows the same pattern as facet visibility initialization.
     * Uses pendingViewFilter to avoid race conditions with pagePreferences.selected.value.
     * Increments viewRefreshKey to force component remount via Vue's :key prop.
     */
    const initializeSettingsFromPreferences = () => {
        const savedFilter = pendingViewFilter;
        
        // Restore colorMode or reset to default
        if (savedFilter?.colorMode) {
            colorMode.value = savedFilter.colorMode as 'TEMPLATES' | 'LOCATIONS' | 'NONE';
        } else {
            colorMode.value = DEFAULT_COLOR_MODE;
        }
        
        // Restore groupBy or reset to default
        if (savedFilter?.groupBy) {
            groupBy.value = savedFilter.groupBy as 'TEAMS' | 'NONE';
        } else {
            groupBy.value = DEFAULT_GROUP_BY;
        }
        
        // Restore columnDisplay or reset to default
        if (savedFilter?.columnDisplay) {
            columnDisplay.value = savedFilter.columnDisplay as { userId: boolean; email: boolean };
        } else {
            columnDisplay.value = { ...DEFAULT_COLUMN_DISPLAY };
        }
        
        // Force component remount to update web component selection state
        viewRefreshKey.value++;
        
        // Clear the pending filter after applying
        pendingViewFilter = null;
    };

    // State
    const pageTitle = ref('Team Schedule');
    const pageSubtitle = ref('');
    const searchTypeSelected = ref<SearchType>('teamSchedule');
    const totalTeamSchedule = ref(0);
    const resetFilters = ref(false);
    const saveTeamScheduleGridSettings = ref(false);
    const restoreTeamScheduleGridSettings = ref(false);
    const exportCSVTeamAndSchedule = ref(false);
    const viewSelected = ref(false);
    const isLoadingView = ref(false);
    const selectedView = ref<'table' | 'timeline'>('table');
    const userTimezone = ref('');
    const userTimeFormatPreference = ref<TimeFormatPreference>({ timeFormat: '24h' });
    const query: Ref<Query> = ref({});
    const facetPanelRef = ref();
    const isFacetPanelVisible = ref(true);
    const timezoneSelected = ref('');
    const colorMode = ref<'TEMPLATES' | 'LOCATIONS' | 'NONE'>(DEFAULT_COLOR_MODE);
    const groupBy = ref<'TEAMS' | 'NONE'>(DEFAULT_GROUP_BY);
    const columnDisplay = ref({ ...DEFAULT_COLUMN_DISPLAY });
    const isAddRemoveUserModalOpen = ref(false);
    const filtersCount = ref(1);
    const maxRecordsForExport = ref(1_000_000);
    
    // Key to force component remount when loading saved views.
    // Incremented in initializeSettingsFromPreferences() to change :key prop value,
    // causing Vue to destroy and recreate al-multi-select components with fresh state.
    const viewRefreshKey = ref(0);

    // Computed
    const getCounter = computed(() => totalTeamSchedule.value);
    const isExportDisabled = computed(() => getCounter.value > maxRecordsForExport.value);
    const timezoneOptions = computed(() => {
        return tz.names().map((zone: string) => ({ id: zone, value: zone }));
    });

    // Watchers
    /**
     * Watch for query changes when a view is selected
     * Similar to how FacetPanel watches query and checks viewSelected flag
     */
    watch(
        () => query.value,
        () => {
            if (viewSelected.value) {
                initializeSettingsFromPreferences();
                // Reset the flag after initialization (following facet pattern)
                viewSelected.value = false;
            }
        },
        { deep: true }
    );

    // Event Handlers
    const handleFacetChange = (selected: unknown) => {
        const selectedData = selected as Record<string, Record<string, unknown>>;
        query.value = {
            ...query.value,
            facets: {
                ...query.value.facets,
                [searchTypeSelected.value]: {
                    ...query.value.facets?.[searchTypeSelected.value],
                    ...selectedData[searchTypeSelected.value]
                }
            } as unknown as Query['facets']
        };
        updatePagePreferences();
        // Store facets in sessionStorage (routed through updateUrlParams)
        try {
            const facetsString = JSON.stringify(query.value.facets);
            updateUrlParams({ facets: facetsString });
        } catch (e) {
            logger.error('Failed to serialize facets for storage', e);
        }
    };

    const handleViewChanged = (selectedLoadView: PagePreference) => {
        setPageTitles(pageTitle, pageSubtitle, selectedLoadView);
        let newQuery: Query = {};
        
        // Check if this is an exit action (when user clicks exit view button)
        // The exit object comes as { exit: true } without a filter property
        const isExitView = !selectedLoadView ||
                          (selectedLoadView as Record<string, unknown>).exit === true ||
                          !selectedLoadView.filter;
        
        if (!isExitView && selectedLoadView) {
            // Loading a saved view - store the filter for reliable restoration
            // This avoids race conditions with pagePreferences.selected.value updates
            pendingViewFilter = selectedLoadView.filter;
            viewSelected.value = true;
            newQuery = { ...selectedLoadView.filter };

            // Persist the saved view's filter state to sessionStorage
            // so it survives page reload without putting data in URL
            updateFilterState({
                facets: newQuery.facets,
                colorMode: newQuery.colorMode,
                groupBy: newQuery.groupBy,
                columnDisplay: newQuery.columnDisplay as { userId: boolean; email: boolean } | undefined
            });
        } else {
            // Exiting a view - reset to defaults
            pendingViewFilter = null;
            viewSelected.value = false;
            colorMode.value = DEFAULT_COLOR_MODE;
            groupBy.value = DEFAULT_GROUP_BY;
            columnDisplay.value = { ...DEFAULT_COLUMN_DISPLAY };
            clearFilterState();
        }
        
        query.value = newQuery;
        const urlParams = buildUrlParamsFromQuery(newQuery, timezoneSelected, userTimezone, selectedView);
        updateUrlParams(urlParams);
        
        // Signal hydration complete after state is set
        nextTick(() => {
            isLoadingView.value = false;
        });
    };

    const handleViewLoaded = (view: PagePreference | undefined) => {
        if (view)
        {
            query.value = { ...query.value, ...view.filter };
            return;
        }
        query.value = { ...query.value };
    };

    const updateTeamSchedule = (total: number) => {
        totalTeamSchedule.value = total;
    };

    const handleToggleChanged = (mode: 'table' | 'timeline') => {
        logger.debug('ToggleBar changed view mode to', mode);
        updateUrlParams({ selectedView: selectedView.value });
        updatePagePreferences();
    };

    const updatePagePreferences = () => {
        updatePagePreferencesUtils({
            query: query.value,
            pagePreferences,
            timezoneSelected,
            userTimezone,
            searchTypeSelected,
            facetPanelRef,
            selectedView,
            colorMode,
            groupBy,
            columnDisplay
        });
    };

    const onTimezoneChange = (event: Event) => {
        const target = event.target as HTMLSelectElement;
        timezoneSelected.value = target.value;
        query.value = { ...query.value, timezone: target.value };
        userTimezone.value = target.value;
        updateUrlParams({ timezone: target.value });
        updateFilterState({ timezone: target.value });
        updatePagePreferences();
    };

    const setupTimezone = async () => {
        const userDataTimezone = await getUserData(['timezone']);
        userTimezone.value = userDataTimezone?.timezone || '';
        timezoneOptions.value.push({ id: userTimezone.value, value: userTimezone.value });
    };

    const setupTimeFormatPreference = async () => {
        userTimeFormatPreference.value = await getUserTimeFormatPreference();
    };

    const openAddRemoveUserModal = () => {
        isAddRemoveUserModalOpen.value = true;
    };

    const closeAddRemoveUserModal = () => {
        isAddRemoveUserModalOpen.value = false;
    };

    const handleColumnDisplaySave = (data: { userId: boolean; email: boolean }) => {
        columnDisplay.value = {
            userId: data.userId,
            email: data.email
        };
        query.value = { ...query.value, columnDisplay: columnDisplay.value };
        // columnDisplay is routed to sessionStorage by updateUrlParams
        updateUrlParams({ columnDisplay: JSON.stringify(columnDisplay.value) });
        updatePagePreferences();
    };

    const runOnMounted = async () => {
        await setupTimezone();
        await setupTimeFormatPreference();
        const urlQuery = buildQueryFromUrl();
        
        // Check if we're loading from a saved view
        const hasViewSelected = urlQuery['view.selected'];
        
        if (!hasViewSelected)
        {
            initializeFromUrlQuery(urlQuery, {
                timezoneSelected,
                userTimezone,
                timezoneOptions,
                selectedView
            });
        
            query.value = buildInitialQuery(urlQuery, timezoneSelected.value, weekStartDay.value);
        }

        if (urlQuery.columnDisplay) {
            columnDisplay.value = {
                userId: urlQuery.columnDisplay.userId ?? false,
                email: urlQuery.columnDisplay.email ?? false
            };
        }
        
        updatePagePreferences();
    };

    const toggleFiltersPanel = () => {
        isFacetPanelVisible.value = !isFacetPanelVisible.value;
        updatePagePreferences();
    };

    const handleFiltersCountChange = (count: number) => {
        filtersCount.value = count;
    };

    /**
     * Handle color mode dropdown change event.
     * Updates colorMode state and persists to page preferences.
     */
    const handleColorModeChange = (event: Event) => {
        const target = event.target as HTMLSelectElement;
        const newValue = target.value;
        if (newValue) {
            colorMode.value = newValue as 'TEMPLATES' | 'LOCATIONS' | 'NONE';
            updatePagePreferences();
        }
    };

    /**
     * Handle group by dropdown change event.
     * Updates groupBy state and persists to page preferences.
     */
    const handleGroupByChange = (event: Event) => {
        const target = event.target as HTMLSelectElement;
        const newValue = target.value;
        if (newValue) {
            groupBy.value = newValue as 'TEAMS' | 'NONE';
            updatePagePreferences();
        }
    };

    // Function to remove colons from dropdown labels and setup listeners
    const setupDropdownCleanup = () => {
        nextTick(() => {
            const dropdowns = [
                { selector: '.view-by-dropdown', needsListener: false },
                { selector: '.group-by-dropdown', needsListener: false },
                { selector: '.search-actions', needsListener: true }
            ];

            dropdowns.forEach(({ selector, needsListener }) => {
                const dropdown = document.querySelector(selector);
                
                if (dropdown && dropdown.shadowRoot) {
                    // Remove existing colons
                    const labelWrapper = dropdown.shadowRoot.querySelector('.label-value-wrapper');
                    if (labelWrapper) {
                        const spans = labelWrapper.querySelectorAll('span');
                        spans.forEach((span) => {
                            if (span.textContent && span.textContent.includes(':')) {
                                span.textContent = span.textContent.replace(/:\s*$/, '');
                            }
                        });
                    }

                    // Setup listeners for Actions dropdown
                    if (needsListener) {
                        const cleanupWithDelay = () => setTimeout(() => setupDropdownCleanup(), 50);
                        
                        dropdown.addEventListener('al-multiselect-open', () => setupDropdownCleanup());
                        dropdown.addEventListener('al-multiselect-close', () => setupDropdownCleanup());
                        dropdown.addEventListener('change', cleanupWithDelay);
                    }
                }
            });
        });
    };

    return {
        // State
        pageTitle,
        pageSubtitle,
        searchTypeSelected,
        totalTeamSchedule,
        resetFilters,
        saveTeamScheduleGridSettings,
        restoreTeamScheduleGridSettings,
        exportCSVTeamAndSchedule,
        viewSelected,
        isLoadingView,
        selectedView,
        userTimezone,
        userTimeFormatPreference,
        query,
        facetPanelRef,
        isFacetPanelVisible,
        timezoneSelected,
        colorMode,
        groupBy,
        columnDisplay,
        isAddRemoveUserModalOpen,
        filtersCount,
        viewRefreshKey,
        // Computed
        getCounter,
        isExportDisabled,
        timezoneOptions,
        // Methods
        handleFacetChange,
        handleViewChanged,
        handleViewLoaded,
        updateTeamSchedule,
        handleToggleChanged,
        updatePagePreferences,
        onTimezoneChange,
        openAddRemoveUserModal,
        closeAddRemoveUserModal,
        handleColumnDisplaySave,
        runOnMounted,
        toggleFiltersPanel,
        handleFiltersCountChange,
        handleColorModeChange,
        handleGroupByChange,
        setupDropdownCleanup
    };
}
